/**
 * Vidéo de présentation du hero — une version par langue de site.
 *
 * Table centralisée EXPRÈS : le JSX ne connaît aucune langue, il ne
 * connaît que « il y a une vidéo, ou pas ». Une langue absente d'ici
 * garde le comportement historique du bouton « Voir comment ça marche »
 * (ancre vers la section `#journey` de la home), et rien n'est alors
 * demandé à Vimeo.
 *
 * ON DÉCLARE DES URL, PAS DES NUMÉROS — et c'est le cœur du fichier.
 * Une adresse Vimeo prend deux formes, et la forme dit le partage : une
 * vidéo PUBLIQUE est `vimeo.com/<id>`, une vidéo NON RÉPERTORIÉE est
 * `vimeo.com/<id>/<hash>`, le hash étant son jeton de confidentialité.
 * Le lecteur refuse de servir une non répertoriée dont le hash manque
 * (403, écran noir « Désolé ») et refuse tout autant une publique à qui
 * l'on colle un `h=` — un numéro seul ne dit donc PAS quelle forme lui
 * revient. Coller l'URL entière telle que Vimeo l'affiche rend l'erreur
 * impossible dans les deux sens ; `parseVimeoUrl` fait le découpage, et
 * `h=` ne part que si le hash est là. Le 14/08/2026, trois identifiants
 * recopiés sans leur hash avaient tué la modale sur les trois langues.
 *
 * Depuis le 24/08/2026 nos trois vidéos sont publiques, donc déclarées
 * sans hash. Repasser l'une d'elles en non répertoriée ne demande rien
 * d'autre que de recoller son URL complète ici. Le garde-fou est
 * ailleurs — si le lecteur refuse la vidéo, la modale retombe sur
 * l'ancre (voir VideoModal).
 */

/**
 * Langue de site → URL Vimeo, copiée-collée depuis la barre d'adresse.
 * Formes acceptées : `vimeo.com/<id>` (publique), `vimeo.com/<id>/<hash>`
 * (non répertoriée), `player.vimeo.com/video/<id>?h=<hash>`.
 */
export const HERO_VIDEO_URL_BY_LANG = {
  fr: 'https://vimeo.com/1220823594',
  nl: 'https://vimeo.com/1220823747',
  en: 'https://vimeo.com/1220823746',
};

/** `vimeo.com/123/abc` → `{ id: '123', hash: 'abc' }` ; sinon `null`. */
export function parseVimeoUrl(url) {
  const s = String(url || '').trim();
  if (!s) return null;
  // Le numéro est le premier groupe de chiffres du chemin ; le hash, s'il
  // existe, le suit soit dans le chemin, soit dans le paramètre `h=`.
  const id = s.match(/(?:vimeo\.com\/(?:video\/)?)(\d+)/)?.[1];
  if (!id) return null;
  const hash = s.match(/[?&]h=([A-Za-z0-9]+)/)?.[1]
    || s.match(new RegExp(`${id}/([A-Za-z0-9]+)`))?.[1]
    || null;
  return hash ? { id, hash } : { id };
}

/** Langue de site → `{ id, hash? }`, dérivé de la table d'URL. */
export const HERO_VIDEO_BY_LANG = Object.fromEntries(
  Object.entries(HERO_VIDEO_URL_BY_LANG)
    .map(([lang, url]) => [lang, parseVimeoUrl(url)])
    .filter(([, video]) => video),
);

/**
 * Vidéo du hero pour une langue, ou `null` si cette langue n'en a pas.
 * `null` est le signal « garde le comportement d'ancre » côté appelant :
 * aucune modale, aucune requête vers Vimeo.
 *
 * @param {string} language  'fr' | 'nl' | 'en' (tolère 'fr-BE', 'FR', …)
 * @returns {{id: string, hash?: string}|null}
 */
export function heroVideoFor(language) {
  const short = String(language || '').slice(0, 2).toLowerCase();
  return HERO_VIDEO_BY_LANG[short] || null;
}

export default HERO_VIDEO_BY_LANG;
